import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAudioContext } from './AudioContext';
import useReactions from '../hooks/useReactions';
import styles from './ReactionOverlay.module.scss';

const EMOJIS = ['🌊', '🌙', '✨', '🔥', '💙'];

const ReactionOverlay = () => {
  const { currentTrackName, currentTimeRef } = useAudioContext();
  const { reactions, addReaction } = useReactions(currentTrackName);
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 768px)').matches
      : false
  );
  const firedRef = useRef(new Set());
  const lastSecondRef = useRef(-1);
  const reactionsRef = useRef(reactions);
  const emojiInnerRefs = useRef({});
  reactionsRef.current = reactions;

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    firedRef.current = new Set();
    lastSecondRef.current = -1;
    setFloatingEmojis([]);
  }, [currentTrackName]);

  useEffect(() => {
    if (!currentTrackName) return;
    const interval = setInterval(() => {
      const s = Math.floor(currentTimeRef.current);
      if (s === lastSecondRef.current) return;
      lastSecondRef.current = s;
      reactionsRef.current.forEach((reaction) => {
        if (Math.floor(reaction.songTimestamp) === s && !firedRef.current.has(reaction.id)) {
          firedRef.current.add(reaction.id);
          spawnEmoji(reaction.emoji, Math.random() * 600);
        }
      });
    }, 500);
    return () => clearInterval(interval);
  }, [currentTrackName, currentTimeRef]);

  const spawnEmoji = (emoji, delayMs = 0) => {
    const id = `${Date.now()}-${Math.random()}`;
    const x = 8 + Math.random() * 84;
    setTimeout(() => {
      setFloatingEmojis((prev) => [...prev, { id, emoji, x }]);
      setTimeout(() => {
        setFloatingEmojis((prev) => prev.filter((e) => e.id !== id));
      }, 2800);
    }, delayMs);
  };

  const handleReaction = (emoji) => {
    if (!currentTrackName) return;

    const el = emojiInnerRefs.current[emoji];
    if (el) {
      el.classList.remove(styles.clicking);
      void el.offsetWidth;
      el.classList.add(styles.clicking);
    }

    spawnEmoji(emoji);
    addReaction(emoji, currentTimeRef.current || 0);
  };

  if (!currentTrackName) return null;

  const buttonBar = (
    <div className={styles.emojiButtons}>
      {EMOJIS.map((emoji) => (
        <button
          key={emoji}
          className={styles.emojiButton}
          onClick={() => handleReaction(emoji)}
          aria-label={`React with ${emoji}`}
        >
          <span ref={el => (emojiInnerRefs.current[emoji] = el)} className={styles.emojiInner}>
            {emoji}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      {/* Full-viewport emoji layer — pointer-events: none so nothing is blocked */}
      {createPortal(
        <div className={styles.emojiLayer}>
          {floatingEmojis.map(({ id, emoji, x }) => (
            <span key={id} className={styles.floatingEmoji} style={{ left: `${x}%` }}>
              {emoji}
            </span>
          ))}
        </div>,
        document.body
      )}

      {/* Mobile: portal the button bar to <body> so `position: fixed` isn't
          trapped by MoodWrapper's containing block (the <video> compositing
          layer on iOS promotes MoodWrapper to a containing block for fixed
          descendants, clipping them to its 240px frame).
          Desktop: keep it inline inside .overlay so it sits over the video. */}
      {isMobile
        ? createPortal(buttonBar, document.body)
        : <div className={styles.overlay}>{buttonBar}</div>}
    </>
  );
};

export default ReactionOverlay;
